import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ProductAvailabilityFacade } from '@availability/data-access';
import { CityService } from '@city/feature';
import { CustomProduct } from '@product/const';
import { SelectedPriceService } from '@product/data-access';
import { DeliverInPipe } from '@product/utils';
import { AddressSuggestionsSelectComponent } from '@sdvor/delivery/ui/v2';
import { SdBottomSheetService } from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { IconTypes } from '@shared/v2';
import { SdBottomSheetOptions } from '@shared/v2/models';
import { CustomPointOfService } from '@shop/const';
import { ShopWorkingTimeService } from '@shop/feature';
import {
  BottomSheetActivePlacemarkComponent,
  IBottomSheetPlacemarkActiveData,
  ShopListComponent,
  ShopMapComponent,
} from '@shop/ui';
import { WindowRef } from '@spartacus/core';
import { GeocodeResult } from '@yandex-maps/const';
import { GeocodeService } from '@yandex-maps/feature';
import { filter, map, shareReplay, switchMap } from 'rxjs';
import { ProductAvailabilityCurrentProductComponent } from './product-availability-current-product';
import { ProductAvailabilityInfoProductComponent } from './product-availability-info-product';

@Component({
  selector: 'sd-product-availability',
  standalone: true,
  imports: [
    ProductAvailabilityCurrentProductComponent,
    ProductAvailabilityInfoProductComponent,
    ShopMapComponent,
    ShopListComponent,
    AddressSuggestionsSelectComponent,
  ],
  templateUrl: './product-availability.component.html',
  styleUrls: ['./product-availability.component.scss'],
  providers: [DeliverInPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductAvailabilityComponent implements OnInit, AfterViewInit {
  readonly product = input.required<CustomProduct>();

  private readonly deviceService = inject(DeviceService);
  private readonly productAvailabilityFacade = inject(ProductAvailabilityFacade);
  private readonly selectedPriceService = inject(SelectedPriceService);
  private readonly geocodeService = inject(GeocodeService);
  private readonly shopWorkingTimeService = inject(ShopWorkingTimeService);
  private readonly bottomSheetService = inject(SdBottomSheetService);
  private readonly cityService = inject(CityService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly deliverInPipe = inject(DeliverInPipe);
  private readonly winRef = inject(WindowRef);

  readonly isDesktopLgUp = toSignal(this.deviceService.isDesktopLgUp$, { initialValue: false });
  readonly iconTypes = IconTypes;
  readonly MAP_ZOOM_ON_SHOP_SELECT = 14;

  @ViewChild('shopListRef') shopListRef: ElementRef<HTMLDivElement>;
  @ViewChild(AddressSuggestionsSelectComponent) addressSelect: AddressSuggestionsSelectComponent;

  readonly shops = signal<CustomPointOfService[]>([]);
  readonly activeShopId = signal<string>('');
  readonly addressGeoObject = signal<GeocodeResult | undefined>(undefined);
  readonly shopsLoaded = signal(false);

  protected openedSearch = signal(false);
  protected searchHasText = signal(false);

  mapInstance: ymaps.Map;

  private readonly product$ = toObservable(this.product);

  private readonly availability = toSignal(
    this.product$.pipe(
      filter(Boolean),
      switchMap((product) => this.productAvailabilityFacade.getAvailability(product.code)),
      shareReplay({ bufferSize: 1, refCount: true }),
    ),
  );

  readonly forceInStock = computed(() => this.availability()?.value?.forceInStock);

  private readonly shopsState$ = this.product$.pipe(
    filter(Boolean),
    switchMap((product) =>
      this.selectedPriceService
        .getSelectedPrice(product.code)
        .pipe(map((price) => ({ product, unitCode: price.unit?.code }))),
    ),
    switchMap(({ product, unitCode }) => this.productAvailabilityFacade.getShopsAvailable(product.code, unitCode)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly shopsSorted = computed(() => {
    const data = this.shops();
    const address = this.addressGeoObject();

    if (!data.length) return data;

    if (address) {
      return data
        .map((shop) => {
          const latitude = shop.geoPoint?.latitude || 0;
          const longitude = shop.geoPoint?.longitude || 0;
          const addressCoords = address.geometry?.getCoordinates() as ymaps.Coordinate;
          const shopCoords: ymaps.Coordinate = [latitude, longitude];

          return { ...shop, distanceKm: this.geocodeService.getDistanceInKm(shopCoords, addressCoords) };
        })
        .sort((a: any, b: any) => (a.distanceKm || 0) - (b.distanceKm || 0));
    }

    return data.slice().sort((a: any, b: any) => (b.available || 0) - (a.available || 0));
  });

  constructor() {
    this.shopsState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      if (state?.value?.pointOfServices) {
        this.shops.set(state.value.pointOfServices);
      }
      if (state?.success || state?.error) {
        this.shopsLoaded.set(true);
      }
    });

    this.destroyRef.onDestroy(() => {
      this.bottomSheetService.close();
    });
  }

  ngOnInit(): void {
    this.cityService.activeBaseStoreCode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const product = this.product();
      if (product) {
        this.productAvailabilityFacade.loadShopsAvailable(product.code, product.unit?.code);
      }
    });
  }

  ngAfterViewInit(): void {
    this.addressSelect.formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.searchHasText.set(!!value);
    });
  }

  onHandleOpened(opened: boolean): void {
    if (opened) {
      this.bottomSheetService.close();
      this.openedSearch.set(opened);
    }
  }

  onChangeAddress(geocodeResult?: GeocodeResult): void {
    this.addressGeoObject.set(geocodeResult);

    if (!geocodeResult) {
      this.removeUserPlacemark();

      return;
    }

    this.openedSearch.set(false);

    if (!this.mapInstance) return;

    this.applyAddressToMap(geocodeResult);
  }

  onMapCreated(ymap: ymaps.Map): void {
    this.mapInstance = ymap;

    const address = this.addressGeoObject();
    if (address) {
      this.applyAddressToMap(address);
    }
  }

  onClickShop(id: string, zoom?: number): void {
    this.openedSearch.set(false);

    if (!this.isDesktopLgUp()) {
      this.openActivePlacemarkBottomSheet(id);

      return;
    }

    this.activeShopId.set(id);
    if (this.mapInstance) {
      if (zoom) {
        this.mapInstance.setZoom(zoom);
      }
      this.selectShopMap(id);
    }
    this.scrollToActiveShop(id);
  }

  private removeUserPlacemark(): void {
    if (!this.mapInstance) return;

    this.mapInstance.geoObjects.each((geoObject) => {
      if (geoObject.options.get('id', {}) === 'userPlacemark') {
        this.mapInstance.geoObjects.remove(geoObject);
      }
    }, {});
  }

  private applyAddressToMap(geocodeResult: GeocodeResult): void {
    const coords = geocodeResult.geometry.getCoordinates();

    if (!coords || !this.mapInstance) return;

    this.mapInstance.setCenter(coords);
    this.mapInstance.setZoom(13);

    this.removeUserPlacemark();

    this.mapInstance.geoObjects.add(
      new ymaps.Placemark(
        coords,
        { iconCaption: geocodeResult.getAddressLine().split(',').slice(1).join(',') || '' },
        {
          id: 'userPlacemark',
          preset: 'islands#orangeCircleDotIcon',
          hasBalloon: true,
          hideIconOnBalloonOpen: false,
          balloonCloseButton: false,
          iconCaptionMaxWidth: '200',
        },
      ),
    );
  }

  onPlacemarkClick(id: string): void {
    if (this.isDesktopLgUp()) {
      this.onClickShop(id);
    } else {
      this.openActivePlacemarkBottomSheet(id);
    }
  }

  getPlacemarksProps = (shop: CustomPointOfService): ymaps.IPlacemarkTemplate => {
    const workingTime = this.shopWorkingTimeService.getWorkingTimeInfo(shop).text;

    return this.getPlacemarksTemplate(shop, workingTime);
  };

  getPlacemarkIconOptions = (shop: CustomPointOfService): Record<string, any> => ({
    iconImageHref: shop.available
      ? 'assets/v2/images/ym/placemark-green.svg'
      : 'assets/v2/images/ym/placemark-orange.svg',
    iconImageSize: [30, 43],
    balloonCloseButton: true,
  });

  getPlacemarksTemplate = (shop: CustomPointOfService, workingTime: string): ymaps.IPlacemarkTemplate => {
    const goToShopBtn = (): string =>
      shop.url ? `<a href="https://sdvor.com${shop.url}" class="shop-link">о магазине</a>` : '';

    const getPickupDateFormatted = (date: string): string => {
      let value: string;
      this.deliverInPipe
        .transform(date, 'd MMMM')
        .subscribe((v) => (value = v))
        .unsubscribe();

      return value;
    };

    const availabilityHtml = (): string => {
      if (shop.available) {
        return `<div class="color-green">
                  в наличии ${shop.available} ${shop.unit?.name || ''}
                </div>`;
      }
      if (shop.pickupDate) {
        const dateFormatted = getPickupDateFormatted(shop.pickupDate);

        return `<div class="color-orange">
                  привезем ${dateFormatted}${dateFormatted === 'сегодня' ? '' : ' при заказе сегодня'}
                </div>`;
      }

      return '';
    };

    return {
      balloonContentHeader: `<div class="text-c1-l-500" style="margin-bottom: -6px;">${shop.addressFormatted}</div>`,
      balloonContentBody: `
        <div class="text-c1-l-300" style="display: flex; flex-direction: column; align-items: flex-start; gap: 8px;">
          <span>${shop.displayName}</span>
          <span>${workingTime}</span>
          ${availabilityHtml()}
          ${goToShopBtn()}
        </div>
      `,
      clusterCaption: shop.addressFormatted || '',
      id: shop.name || '',
    };
  };

  private selectShopMap(id: string): void {
    if (!this.mapInstance) return;

    const openBalloon = (placemark: ymaps.Placemark): void => {
      if (id === placemark.properties?.get<string>('id', '')) {
        const coord = placemark.geometry?.getCoordinates();
        if (coord) {
          this.mapInstance.panTo(coord).then(() => {
            placemark.balloon?.open();
          });
        }
      }
    };

    this.mapInstance.geoObjects.each((geoObject: ymaps.Placemark) => {
      switch (typeof geoObject.getGeoObjects) {
        case 'function':
          geoObject.getGeoObjects().forEach((mark) => {
            openBalloon(mark as ymaps.Placemark);
          });
          break;
        default:
          openBalloon(geoObject);
      }
    }, {});
  }

  private scrollToActiveShop(id: string): void {
    const shop = this.shopListRef.nativeElement.querySelector(`#shop-${id}`) as HTMLDivElement;

    if (shop) {
      this.shopListRef.nativeElement.scrollTo({
        top: shop.offsetTop - this.shopListRef.nativeElement.offsetTop,
        behavior: 'smooth',
      });
    }
  }

  private openActivePlacemarkBottomSheet(id: string): void {
    const shop = this.shops().find((s) => s.name === id);
    if (!shop) return;

    if (!this.winRef.isBrowser()) {
      return;
    }

    const height = 210;
    const window = this.winRef.nativeWindow;

    const maxHeight = (height / (window?.innerHeight || height)) * 100;

    const options: SdBottomSheetOptions<IBottomSheetPlacemarkActiveData> = {
      breakpoints: [maxHeight],
      init: { type: 'px', value: height },
      minHeight: { type: 'px', value: height },
      closeDesktopDown: true,
      hasCloseButton: true,
      ignoreOutsideClick: false,
      blockOverlay: false,
      background: 'white',
      overlayDismissMode: 'collapse',
      data: { shop, showAvailability: true },
      onClose: () => {},
      hideAnchor: true,
      styleContainer: {
        bottom: '58px',
        'box-shadow': '0 -2px 6px -2px rgba(65, 66, 68, 0.1) inset',
      },
    };

    this.bottomSheetService.open(BottomSheetActivePlacemarkComponent, options);
  }
}
